import {inject, provider} from "react-ioc";
import {observer} from "mobx-react";
import React, {Component} from "react";
import {Container, ContainerDetails} from "app/modules/common/layout/components/containers";
import {PageTitleDetails} from "app/modules/common/layout/components/page-title";
import {Loading} from "app/modules/common/layout/components/loading";
import {Grid, MenuItem, Stack,  Paper, IconButton, Button, Typography, Box, Divider} from "@mui/material";
import {SectionSelectField, SectionTextField} from "app/modules/common/details/text-field";
import {ApiClients} from "app/modules/company/services/AxiosBaseClient";
import {IntegrationSetting, IntegrationSettingType} from "app/modules/company/api/clients.api";
import {IntegrationSettingOrderParserType, SubjectRuleType, EmailFilterType} from "app/modules/company/types/integration";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {BaseLoadingStore} from "app/modules/common/stores/BaseLoadingStore";
import {action, makeObservable, observable, runInAction} from "mobx";
import {FieldState, FormState} from "formstate";
import {required, email} from "app/modules/common/form/validators";

const eitherFromOrSubjectRuleRequired = (getOtherValue: () => string) => (value: string) => {
    if (!value && !getOtherValue()) {
        return 'Either From Email or Subject Rule must be provided';
    }
    return null;
};

export class IntegrationSettingsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;

    integrationId: number;
    filterSettings: IntegrationSetting[] = [];
    orderAiParserSettings: IntegrationSetting[] = [];
    filterFormStates: Map<number, FormState<any>> = new Map();
    orderAiParserFormStates: Map<number, FormState<any>> = new Map();
    newFilterFormState: FormState<any> | null = null;
    newOrderAiParserFormState: FormState<any> | null = null;
    editingFilterId: number | null = null;
    editingOrderAiParserId: number | null = null;

    constructor() {
        super();
        makeObservable(this, {
            filterSettings: observable,
            orderAiParserSettings: observable,
            filterFormStates: observable,
            orderAiParserFormStates: observable,
            newFilterFormState: observable,
            newOrderAiParserFormState: observable,
            editingFilterId: observable,
            editingOrderAiParserId: observable,
            init: action,
            loadSettings: action,
            addFilterSetting: action,
            addOrderAiParserSetting: action,
            deleteSetting: action,
            saveSetting: action,
            showNewFilterForm: action,
            showNewOrderAiParserForm: action,
            cancelNewSetting: action,
            setEditingFilterId: action,
            setEditingOrderAiParserId: action
        });
    }

    init = async (integrationId: number) => {
        this.integrationId = integrationId;
        await this.loadSettings();
    }

    loadSettings = async () => {
        await this.wrapInitLoadingApiCall(async () => {
            const settings = await this.companyApiClients.companyIntegrationClient.getIntegrationSettings(this.integrationId);

            runInAction(() => {
                this.filterSettings = settings.filter(s => s.type === IntegrationSettingType.Filter);
                this.orderAiParserSettings = settings.filter(s => s.type === IntegrationSettingType.OrderAiParser);

                // Initialize form states for existing settings
                this.filterSettings.forEach(setting => {
                    this.filterFormStates.set(setting.id, this.createFilterFormState(setting));
                });

                this.orderAiParserSettings.forEach(setting => {
                    this.orderAiParserFormStates.set(setting.id, this.createOrderAiParserFormState(setting));
                });
            });
        });
    }

    createFilterFormState = (setting?: IntegrationSetting) => {
        let filterValue = '';
        let filterType = EmailFilterType.Email;
        if (setting?.settingsJSON) {
            try {
                const parsed = JSON.parse(setting.settingsJSON);
                if (parsed.Filter) {
                    filterValue = parsed.Filter.Value || '';
                    filterType = parsed.Filter.Type ?? EmailFilterType.Email;
                }
            } catch {}
        }
        return new FormState({
            name: new FieldState(setting?.name || '').validators(required),
            filterValue: new FieldState(filterValue),
            filterType: new FieldState(filterType),
            id: new FieldState<number>(setting?.id || null)
        });
    }

    createOrderAiParserFormState = (setting?: IntegrationSetting) => {
        const settings = setting?.settingsJSON ? JSON.parse(setting.settingsJSON) : {};
        const formState = new FormState({
            name: new FieldState(setting?.name || '').validators(required),
            from: new FieldState(''),
            subjectRule: new FieldState(''),
            subjectRuleType: new FieldState(settings.SubjectRuleType || SubjectRuleType.Contains).validators(required),
            type: new FieldState(settings.Type || IntegrationSettingOrderParserType.AI).validators(required),
            id: new FieldState<number>(setting?.id || null)
        });

        formState.$.from.value = settings.From || '';
        formState.$.subjectRule.value = settings.SubjectRule || '';
        formState.$.subjectRuleType.value = settings.SubjectRuleType || SubjectRuleType.Contains;
        formState.$.type.value = settings.Type || IntegrationSettingOrderParserType.AI;

        formState.$.from.validators(
            eitherFromOrSubjectRuleRequired(() => formState.$.subjectRule.value || ''),
            email
        );
        formState.$.subjectRule.validators(
            eitherFromOrSubjectRuleRequired(() => formState.$.from.value || '')
        );

        return formState;
    }

    getFilterFormState = (id: number) => {
        return this.filterFormStates.get(id);
    }

    getOrderAiParserFormState = (id: number) => {
        return this.orderAiParserFormStates.get(id);
    }

    showNewFilterForm = () => {
        this.newFilterFormState = this.createFilterFormState();
    }

    showNewOrderAiParserForm = () => {
        this.newOrderAiParserFormState = this.createOrderAiParserFormState();
    }

    cancelNewSetting = () => {
        this.newFilterFormState = null;
        this.newOrderAiParserFormState = null;
    }

    addFilterSetting = async () => {
        if (!this.newFilterFormState) {
            this.showNewFilterForm();
            return;
        }

        await this.newFilterFormState.validate();
        if (this.newFilterFormState.hasError) return;

        const settings = {
            Filter: {
                Value: this.newFilterFormState.$.filterValue.value || '',
                Type: this.newFilterFormState.$.filterType.value
            }
        };

        const setting = await this.companyApiClients.companyIntegrationClient.upsertIntegrationSetting(
            this.integrationId,
            this.newFilterFormState.$.id.value,
            this.newFilterFormState.$.name.value,
            IntegrationSettingType.Filter,
            JSON.stringify(settings)
        );

        runInAction(() => {
            this.filterSettings.push(setting);
            this.filterFormStates.set(setting.id, this.newFilterFormState!);
            this.newFilterFormState = null;
        });
    }

    addOrderAiParserSetting = async () => {
        if (!this.newOrderAiParserFormState) {
            this.showNewOrderAiParserForm();
            return;
        }

        await this.newOrderAiParserFormState.validate();
        if (this.newOrderAiParserFormState.hasError) return;

        const settings = {
            From: this.newOrderAiParserFormState.$.from.value || '',
            SubjectRule: this.newOrderAiParserFormState.$.subjectRule.value || '',
            SubjectRuleType: this.newOrderAiParserFormState.$.subjectRuleType.value,
            Type: this.newOrderAiParserFormState.$.type.value
        };

        const setting = await this.companyApiClients.companyIntegrationClient.upsertIntegrationSetting(
            this.integrationId,
            this.newOrderAiParserFormState.$.id.value,
            this.newOrderAiParserFormState.$.name.value,
            IntegrationSettingType.OrderAiParser,
            JSON.stringify(settings)
        );

        runInAction(() => {
            this.orderAiParserSettings.push(setting);
            this.orderAiParserFormStates.set(setting.id, this.newOrderAiParserFormState!);
            this.newOrderAiParserFormState = null;
        });
    }

    deleteSetting = async (id: number) => {
        await this.companyApiClients.companyIntegrationClient.deleteIntegrationSetting(this.integrationId, id);
        await this.loadSettings();
    }

    setEditingFilterId = (id: number | null) => {
        this.editingFilterId = id;
    }

    setEditingOrderAiParserId = (id: number | null) => {
        this.editingOrderAiParserId = id;
    }

    saveSetting = async (id: number, type: IntegrationSettingType) => {
        const formState = type === IntegrationSettingType.Filter
            ? this.filterFormStates.get(id)
            : this.orderAiParserFormStates.get(id);

        if (!formState) return;

        await formState.validate();
        if (formState.hasError) return;

        let settings;
        if (type === IntegrationSettingType.Filter) {
            settings = {
                Filter: {
                    Value: formState.$.filterValue.value,
                    Type: formState.$.filterType.value
                }
            };
        } else {
            settings = {
                From: formState.$.from.value,
                SubjectRule: formState.$.subjectRule.value,
                SubjectRuleType: formState.$.subjectRuleType.value,
                Type: formState.$.type.value
            };
        }

        await this.companyApiClients.companyIntegrationClient.upsertIntegrationSetting(
            this.integrationId,
            formState.$.id.value,
            formState.$.name.value,
            type,
            JSON.stringify(settings)
        );

        await this.loadSettings();
        if (type === IntegrationSettingType.Filter) {
            this.setEditingFilterId(null);
        } else {
            this.setEditingOrderAiParserId(null);
        }
    }
}

@provider(IntegrationSettingsStore)
@observer
class IntegrationSettings extends Component<{ integrationId: number }> {
    @inject integrationSettingsStore: IntegrationSettingsStore;

    componentDidMount() {
        this.integrationSettingsStore.init(this.props.integrationId);
    }

    render() {
        if (this.integrationSettingsStore.isInitLoading) {
            return <Loading/>;
        }

        return <Container>
            <PageTitleDetails Title="Integration Settings" Size={12}/>
            <ContainerDetails>
                <Grid container spacing={2}>
                    {/* Filter Settings Section */}
                    <Grid item xs={6}>
                        <Paper elevation={2} sx={{p: 2}}>
                            <Typography variant="h6">Filter Settings</Typography>
                            <Typography variant="body2">Configure email filtering rules</Typography>
                            <Box sx={{mt: 2, width: "100%"}}>
                                <Stack spacing={2}>
                                    {this.integrationSettingsStore.filterSettings.map((setting, index) => {
                                        const form = this.integrationSettingsStore.getFilterFormState(setting.id);
                                        const isEditing = this.integrationSettingsStore.editingFilterId === setting.id;
                                        return (
                                            <Stack key={setting.id} spacing={2}>
                                                <Divider sx={{mt: 2}} />
                                                {isEditing ? (
                                                    <>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <SectionTextField
                                                                label="Setting Name"
                                                                field={form.$.name}
                                                                fullWidth
                                                            />
                                                            <IconButton
                                                                onClick={() => this.integrationSettingsStore.deleteSetting(setting.id)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Stack>
                                                        <SectionTextField
                                                            label="Filter Value"
                                                            field={form.$.filterValue}
                                                            fullWidth
                                                        />
                                                        <SectionSelectField
                                                            label="Filter Type"
                                                            field={form.$.filterType}
                                                            none={false}
                                                            width="100%"
                                                            items={() => Object.entries(EmailFilterType)
                                                                .filter(([key]) => isNaN(Number(key)))
                                                                .map(([key, value]) => (
                                                                    <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                                ))
                                                            }
                                                        />
                                                        <Stack direction="row" spacing={2}>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.saveSetting(setting.id, IntegrationSettingType.Filter)}
                                                                variant="contained"
                                                                color="primary"
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.setEditingFilterId(null)}
                                                                variant="outlined"
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Stack>
                                                        <Divider sx={{mt: 2}} />
                                                    </>
                                                ) : (
                                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight="bold">{form.$.name.value}</Typography>
                                                            {form.$.filterValue.value && (
                                                                <Typography variant="body2">{form.$.filterValue.value}</Typography>
                                                            )}
                                                            {form.$.filterType.value !== undefined && form.$.filterType.value !== null && (
                                                                <Typography variant="body2">{EmailFilterType[form.$.filterType.value]}</Typography>
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.setEditingFilterId(setting.id)}
                                                                variant="outlined"
                                                                sx={{mr: 1}}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <IconButton
                                                                onClick={() => this.integrationSettingsStore.deleteSetting(setting.id)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        );
                                    })}
                                    {this.integrationSettingsStore.newFilterFormState && (
                                        <Stack spacing={2}>
                                            <Divider sx={{mt: 2}} />
                                            <SectionTextField
                                                label="Setting Name"
                                                field={this.integrationSettingsStore.newFilterFormState.$.name}
                                                fullWidth
                                            />
                                            <SectionTextField
                                                label="Filter Value"
                                                field={this.integrationSettingsStore.newFilterFormState.$.filterValue}
                                                fullWidth
                                            />
                                            <SectionSelectField
                                                label="Filter Type"
                                                field={this.integrationSettingsStore.newFilterFormState.$.filterType}
                                                none={false}
                                                width="100%"
                                                items={() => Object.entries(EmailFilterType)
                                                    .filter(([key]) => isNaN(Number(key)))
                                                    .map(([key, value]) => (
                                                        <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                    ))
                                                }
                                            />
                                            <Stack direction="row" spacing={2}>
                                                <Button
                                                    onClick={() => this.integrationSettingsStore.addFilterSetting()}
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    onClick={() => this.integrationSettingsStore.cancelNewSetting()}
                                                    variant="outlined"
                                                >
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                            {!this.integrationSettingsStore.newFilterFormState && (
                            <Button
                                startIcon={<AddIcon/>}
                                onClick={() => this.integrationSettingsStore.showNewFilterForm()}
                                variant="outlined"
                                sx={{mt: 2}}
                            >
                                Add Filter
                            </Button>
                        )}
                        </Paper>
                    </Grid>

                    {/* OrderAiParser Settings Section */}
                    <Grid item xs={6}>
                        <Paper elevation={2} sx={{p: 2}}>
                            <Typography variant="h6">Order AI Parser Settings</Typography>
                            <Typography variant="body2">Configure parsing rules for orders</Typography>
                            <Box sx={{mt: 2, width: "100%"}}>
                                <Stack spacing={2}>
                                    {this.integrationSettingsStore.orderAiParserSettings.map((setting, index) => {
                                        const form = this.integrationSettingsStore.getOrderAiParserFormState(setting.id);
                                        const isEditing = this.integrationSettingsStore.editingOrderAiParserId === setting.id;
                                        return (
                                            <Stack key={setting.id} spacing={2}>
                                                <Divider sx={{mt: 2}} />
                                                {isEditing ? (
                                                    <>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <SectionTextField
                                                                label="Setting Name"
                                                                field={form.$.name}
                                                                fullWidth
                                                            />
                                                            <IconButton
                                                                onClick={() => this.integrationSettingsStore.deleteSetting(setting.id)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Stack>
                                                        <SectionTextField
                                                            label="From Email"
                                                            field={form.$.from}
                                                            fullWidth
                                                        />
                                                        <SectionTextField
                                                            label="Subject Rule"
                                                            field={form.$.subjectRule}
                                                            fullWidth
                                                        />
                                                        <SectionSelectField
                                                            field={form.$.subjectRuleType}
                                                            label="Subject Rule Type"
                                                            none={false}
                                                            width="100%"
                                                            items={() => Object.entries(SubjectRuleType)
                                                                .filter(([key]) => isNaN(Number(key)))
                                                                .map(([key, value]) => (
                                                                    <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                                ))
                                                            }
                                                        />
                                                        <SectionSelectField
                                                            field={form.$.type}
                                                            label="Parser Type"
                                                            none={false}
                                                            width="100%"
                                                            items={() => Object.entries(IntegrationSettingOrderParserType)
                                                                .filter(([key]) => isNaN(Number(key)))
                                                                .map(([key, value]) => (
                                                                    <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                                ))
                                                            }
                                                        />
                                                        <Stack direction="row" spacing={2}>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.saveSetting(setting.id, IntegrationSettingType.OrderAiParser)}
                                                                variant="contained"
                                                                color="primary"
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.setEditingOrderAiParserId(null)}
                                                                variant="outlined"
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Stack>
                                                        <Divider sx={{mt: 2}} />
                                                    </>
                                                ) : (
                                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight="bold">{form.$.name.value}</Typography>
                                                            {form.$.from.value && (
                                                                <Typography variant="body2">From: {form.$.from.value}</Typography>
                                                            )}
                                                            {form.$.subjectRule.value && (
                                                                <Typography variant="body2">Subject Rule: {form.$.subjectRule.value}</Typography>
                                                            )}
                                                            {form.$.subjectRule.value && form.$.subjectRuleType.value !== undefined && form.$.subjectRuleType.value !== null && (
                                                                <Typography variant="body2">{SubjectRuleType[form.$.subjectRuleType.value]}</Typography>
                                                            )}
                                                            {form.$.type.value !== undefined && form.$.type.value !== null && (
                                                                <Typography variant="body2">Parser Type: {IntegrationSettingOrderParserType[form.$.type.value]}</Typography>
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <Button
                                                                onClick={() => this.integrationSettingsStore.setEditingOrderAiParserId(setting.id)}
                                                                variant="outlined"
                                                                sx={{mr: 1}}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <IconButton
                                                                onClick={() => this.integrationSettingsStore.deleteSetting(setting.id)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Box>
                            {this.integrationSettingsStore.newOrderAiParserFormState && (
                                <Box sx={{mt: 2, width: "100%"}}>
                                    <Divider sx={{mt: 2}} />
                                    <Stack spacing={2}>
                                        <SectionTextField
                                            label="Setting Name"
                                            field={this.integrationSettingsStore.newOrderAiParserFormState.$.name}
                                            fullWidth
                                        />
                                        <SectionTextField
                                            label="From Email"
                                            field={this.integrationSettingsStore.newOrderAiParserFormState.$.from}
                                            fullWidth
                                        />
                                        <SectionTextField
                                            label="Subject Rule"
                                            field={this.integrationSettingsStore.newOrderAiParserFormState.$.subjectRule}
                                            fullWidth
                                        />
                                        <SectionSelectField
                                            field={this.integrationSettingsStore.newOrderAiParserFormState.$.subjectRuleType}
                                            label="Subject Rule Type"
                                            none={false}
                                            width="100%"
                                            items={() => Object.entries(SubjectRuleType)
                                                .filter(([key]) => isNaN(Number(key)))
                                                .map(([key, value]) => (
                                                    <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                ))
                                            }
                                        />
                                        <SectionSelectField
                                            field={this.integrationSettingsStore.newOrderAiParserFormState.$.type}
                                            label="Parser Type"
                                            none={false}
                                            width="100%"
                                            items={() => Object.entries(IntegrationSettingOrderParserType)
                                                .filter(([key]) => isNaN(Number(key)))
                                                .map(([key, value]) => (
                                                    <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                                ))
                                            }
                                        />
                                        <Stack direction="row" spacing={2}>
                                            <Button
                                                onClick={() => this.integrationSettingsStore.addOrderAiParserSetting()}
                                                variant="contained"
                                                color="primary"
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                onClick={() => this.integrationSettingsStore.cancelNewSetting()}
                                                variant="outlined"
                                            >
                                                Cancel
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>
                             )}
                            {!this.integrationSettingsStore.newOrderAiParserFormState && (
                                <Button
                                    sx={{mt: 2}}
                                    startIcon={<AddIcon/>}
                                    onClick={() => this.integrationSettingsStore.addOrderAiParserSetting()}
                                    variant="outlined">
                                    Add Order AI Parser
                                </Button>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </ContainerDetails>
        </Container>;
    }
}

export default IntegrationSettings;
